/**
 * The mail composer's WYSIWYG body (Spark/Gmail-style): what you see is styled text, while the draft stays the
 * composer's Slack-flavoured markdown (`*bold*`, `_italic_`, `~strike~`, `` `code` ``, `[text](url)`; apps/api
 * richtext/parse, mail/compose), so drafts, templates, signatures, Ask AI and sending keep working unchanged.
 *
 * Runs are the visible text with its marks; `runsOfMarkdown` / `markdownOfRuns` convert, and the edit helpers work in
 * visible offsets. Line syntax (`- ` / `1. ` lists, `> ` quotes, ``` fences) stays visible text: it reads naturally and
 * the server already formats it. Formatting never spans lines, as in the parser.
 */

export type MailMark = "bold" | "italic" | "strike" | "code";

export type MailRun = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  strike?: boolean;
  code?: boolean;
  link?: string;
};

/** The marks a run carries (the text left out). */
export type MailMarks = Omit<MailRun, "text">;

const DELIMS: readonly { token: string; mark: MailMark }[] = [
  { token: "**", mark: "bold" },
  { token: "~~", mark: "strike" },
  { token: "*", mark: "bold" },
  { token: "_", mark: "italic" },
  { token: "~", mark: "strike" },
];
const WRAP: readonly { mark: "bold" | "italic" | "strike"; token: string }[] = [
  { mark: "bold", token: "*" },
  { mark: "italic", token: "_" },
  { mark: "strike", token: "~" },
];
const MD_LINK_RE = /^\[([^\]\n]+)\]\(((?:https?:\/\/|mailto:)[^\s()]+(?:\([^\s()]*\)[^\s()]*)*)\)/i;
const ANGLE_LINK_RE = /^<((?:https?:\/\/|mailto:)[^\s<>|]+)\|([^<>\n]+)>/i;
const FENCE_RE = /^\s{0,3}```/;
const WORD = /[\p{L}\p{N}_]/u;
const SPACE = /\s/u;

const isWord = (c: string | undefined) => c !== undefined && WORD.test(c);
const isSpace = (c: string | undefined) => c !== undefined && SPACE.test(c);

type Char = { ch: string; marks: MailMarks };

function sameMarks(a: MailMarks, b: MailMarks): boolean {
  return a.bold === b.bold && a.italic === b.italic && a.strike === b.strike && a.code === b.code && a.link === b.link;
}

function clean(m: MailMarks): MailMarks {
  const out: MailMarks = {};
  if (m.bold === true) out.bold = true;
  if (m.italic === true) out.italic = true;
  if (m.strike === true) out.strike = true;
  if (m.code === true) out.code = true;
  if (m.link !== undefined) out.link = m.link;
  return out;
}

function charsOf(runs: readonly MailRun[]): Char[] {
  const out: Char[] = [];
  for (const r of runs) {
    const { text, ...marks } = r;
    const m = clean(marks);
    for (const ch of text.split("")) out.push({ ch, marks: m });
  }
  return out;
}

function runsOfChars(chars: readonly Char[]): MailRun[] {
  const out: MailRun[] = [];
  for (const c of chars) {
    const last = out.at(-1);
    if (last !== undefined && sameMarks(clean(last), c.marks)) last.text += c.ch;
    else out.push({ text: c.ch, ...c.marks });
  }
  return out;
}

/** Same as the parser's findCloser: the closing token on this line, not after a space nor before a word. */
function findCloser(s: string, from: number, token: string): number {
  let j = from;
  while (j < s.length) {
    if (s[j] === "`") {
      const end = s.indexOf("`", j + 1);
      if (end > j) {
        j = end + 1;
        continue;
      }
    }
    if (s.startsWith(token, j) && j > from) {
      const after = s[j + token.length];
      if (!isSpace(s[j - 1]) && !isWord(after) && after !== token[0]) return j;
    }
    j++;
  }
  return -1;
}

function parseLine(s: string, marks: MailMarks, out: Char[]): void {
  const push = (text: string, m: MailMarks) => {
    for (const ch of text.split("")) out.push({ ch, marks: m });
  };
  let i = 0;
  while (i < s.length) {
    const c = s[i]!;
    const prev = i > 0 ? s[i - 1] : undefined;
    const rest = s.slice(i);
    if (c === "`") {
      const end = s.indexOf("`", i + 1);
      if (end > i + 1) {
        push(s.slice(i + 1, end), clean({ ...marks, code: true }));
        i = end + 1;
        continue;
      }
    }
    if (c === "[" || c === "<") {
      const m = c === "[" ? MD_LINK_RE.exec(rest) : ANGLE_LINK_RE.exec(rest);
      if (m !== null) {
        const [text, url] = c === "[" ? [m[1]!, m[2]!] : [m[2]!.trim(), m[1]!];
        push(text, clean({ ...marks, link: url }));
        i += m[0].length;
        continue;
      }
    }
    if ((c === "*" || c === "_" || c === "~") && !isWord(prev) && prev !== c) {
      let handled = false;
      for (const d of DELIMS) {
        if (!rest.startsWith(d.token)) continue;
        const from = i + d.token.length;
        const first = s[from];
        if (first === undefined || isSpace(first) || first === d.token[0]) continue;
        const close = findCloser(s, from, d.token);
        if (close < 0) continue;
        parseLine(s.slice(from, close), clean({ ...marks, [d.mark]: true }), out);
        i = close + d.token.length;
        handled = true;
        break;
      }
      if (handled) continue;
    }
    push(c, marks);
    i++;
  }
}

/** The composer's markdown → the runs the editor shows (markers hidden, their text styled). */
export function runsOfMarkdown(markdown: string): MailRun[] {
  const chars: Char[] = [];
  let fenced = false;
  markdown.split("\n").forEach((line, n) => {
    if (n > 0) chars.push({ ch: "\n", marks: {} });
    if (FENCE_RE.test(line)) fenced = !fenced;
    if (fenced || FENCE_RE.test(line)) {
      for (const ch of line.split("")) chars.push({ ch, marks: {} });
      return;
    }
    parseLine(line, {}, chars);
  });
  return runsOfChars(chars);
}

/**
 * A mark whose span ends on a space or inside a word wouldn't parse back (`*bold *`, `un*bold*`): move it off the
 * spaces at its ends, and widen it to whole words. Links only lose the spaces at their ends.
 */
function fitMarks(line: Char[]): Char[] {
  const out = line.map((c) => ({ ch: c.ch, marks: { ...c.marks } }));
  for (const { mark } of WRAP) {
    let i = 0;
    while (i < out.length) {
      if (out[i]!.marks[mark] !== true) {
        i++;
        continue;
      }
      let j = i;
      while (j < out.length && out[j]!.marks[mark] === true) j++;
      let a = i;
      let b = j;
      while (a < b && isSpace(out[a]!.ch)) out[a++]!.marks[mark] = undefined;
      while (b > a && isSpace(out[b - 1]!.ch)) out[--b]!.marks[mark] = undefined;
      if (a < b) {
        while (a > 0 && isWord(out[a - 1]!.ch) && isWord(out[a]!.ch)) out[--a]!.marks[mark] = true;
        while (b < out.length && isWord(out[b]!.ch) && isWord(out[b - 1]!.ch)) out[b++]!.marks[mark] = true;
      }
      i = Math.max(b, j);
    }
  }
  // A link keeps its own span; only the spaces at its ends leave it.
  let i = 0;
  while (i < out.length) {
    const url = out[i]!.marks.link;
    if (url === undefined) {
      i++;
      continue;
    }
    let j = i;
    while (j < out.length && out[j]!.marks.link === url) j++;
    for (let a = i; a < j && isSpace(out[a]!.ch); a++) out[a]!.marks.link = undefined;
    for (let b = j - 1; b >= i && isSpace(out[b]!.ch); b--) out[b]!.marks.link = undefined;
    i = j;
  }
  return out.map((c) => ({ ch: c.ch, marks: clean(c.marks) }));
}

function base(chars: readonly Char[]): string {
  const m = chars[0]!.marks;
  const text = chars.map((c) => c.ch).join("");
  if (m.link !== undefined) return text === m.link ? text : `[${text.replace(/[[\]]/g, "")}](${m.link})`;
  if (m.code === true) return text.includes("`") ? text : `\`${text}\``;
  return text;
}

/**
 * One line's characters → markdown. Markers open and close like a stack (`_*a* b_`); of the marks starting together,
 * the one reaching further opens first, so it closes last and the markers nest.
 */
const token = (mark: "bold" | "italic" | "strike") => WRAP.find((w) => w.mark === mark)!.token;

function emitLine(chars: readonly Char[]): string {
  const reach = (i: number, mark: "bold" | "italic" | "strike") => {
    let j = i;
    while (j < chars.length && chars[j]!.marks[mark] === true) j++;
    return j;
  };
  const open: ("bold" | "italic" | "strike")[] = [];
  let out = "";
  let i = 0;
  while (i < chars.length) {
    const m = chars[i]!.marks;
    const keep = open.findIndex((x) => m[x] !== true);
    if (keep >= 0) while (open.length > keep) out += token(open.pop()!);
    const starting = WRAP.map((w) => w.mark)
      .filter((x) => m[x] === true && !open.includes(x))
      .toSorted((x, y) => reach(i, y) - reach(i, x));
    for (const x of starting) {
      out += token(x);
      open.push(x);
    }
    let j = i + 1;
    while (j < chars.length && sameMarks(chars[j]!.marks, m)) j++;
    out += base(chars.slice(i, j));
    i = j;
  }
  while (open.length > 0) out += token(open.pop()!);
  return out;
}

/** The editor's runs → the composer's markdown (what drafts store and the server sends). */
export function markdownOfRuns(runs: readonly MailRun[]): string {
  const lines: Char[][] = [[]];
  for (const c of charsOf(runs)) {
    if (c.ch === "\n") lines.push([]);
    else lines.at(-1)!.push(c);
  }
  let fenced = false;
  return lines
    .map((line) => {
      const text = line.map((c) => c.ch).join("");
      if (FENCE_RE.test(text)) {
        fenced = !fenced;
        return text;
      }
      return fenced ? text : emitLine(fitMarks(line));
    })
    .join("\n");
}

/** The runs' visible text. */
export function plainText(runs: readonly MailRun[]): string {
  return runs.map((r) => r.text).join("");
}

/**
 * The marks typing at [start, end) takes: those of the character before (the first selected one when the selection
 * starts a line), except a link, which only continues when typing inside it.
 */
export function marksAt(runs: readonly MailRun[], start: number, end: number): MailMarks {
  const chars = charsOf(runs);
  const s = Math.max(0, Math.min(start, end, chars.length));
  const e = Math.min(chars.length, Math.max(start, end));
  if (s < e) {
    // A selection: the marks every selected character has.
    const sel = chars.slice(s, e).filter((c) => c.ch !== "\n");
    if (sel.length === 0) return {};
    const all = (k: MailMark) => sel.every((c) => c.marks[k] === true);
    const link = sel[0]!.marks.link;
    return clean({
      bold: all("bold"),
      italic: all("italic"),
      strike: all("strike"),
      code: all("code"),
      ...(link !== undefined && sel.every((c) => c.marks.link === link) ? { link } : {}),
    });
  }
  const before = s > 0 && chars[s - 1]!.ch !== "\n" ? chars[s - 1]!.marks : undefined;
  if (before === undefined) return {};
  const after = s < chars.length ? chars[s]!.marks : undefined;
  const { link, ...rest } = before;
  return clean({
    ...rest,
    ...(link !== undefined && after?.link === link ? { link } : {}),
  });
}

/** [start, end) of the visible text → `inserted`, styled with `marks` (default: marksAt). */
export function replaceText(runs: readonly MailRun[], start: number, end: number, inserted: string, marks?: MailMarks): MailRun[] {
  const chars = charsOf(runs);
  const s = Math.max(0, Math.min(start, end, chars.length));
  const e = Math.min(chars.length, Math.max(start, end));
  const m = clean(marks ?? marksAt(runs, s, s));
  const added = inserted.split("").map((ch): Char => ({ ch, marks: ch === "\n" ? {} : m }));
  return runsOfChars([...chars.slice(0, s), ...added, ...chars.slice(e)]);
}

/** ⌘B / ⌘I / ⌘⇧X / ⌘E on [start, end): off when every selected character has it, else on for all of them. */
export function toggleMark(runs: readonly MailRun[], start: number, end: number, mark: MailMark): MailRun[] {
  const chars = charsOf(runs);
  const s = Math.max(0, Math.min(start, end, chars.length));
  const e = Math.min(chars.length, Math.max(start, end));
  if (s === e) return runsOfChars(chars);
  const on = marksAt(runs, s, e)[mark] !== true;
  return runsOfChars(chars.map((c, i) => (i < s || i >= e || c.ch === "\n" ? c : { ch: c.ch, marks: clean({ ...c.marks, [mark]: on }) })));
}

/** Links [start, end) to `url` (null: unlinks it). */
export function setLink(runs: readonly MailRun[], start: number, end: number, url: string | null): MailRun[] {
  const chars = charsOf(runs);
  const s = Math.max(0, Math.min(start, end, chars.length));
  const e = Math.min(chars.length, Math.max(start, end));
  return runsOfChars(chars.map((c, i) => (i < s || i >= e || c.ch === "\n" ? c : { ch: c.ch, marks: clean({ ...c.marks, link: url ?? undefined }) })));
}

/** The link around the caret or selection: its text range and address. */
export function linkAt(runs: readonly MailRun[], start: number, end: number): { start: number; end: number; url: string } | null {
  const chars = charsOf(runs);
  const s = Math.max(0, Math.min(start, end, chars.length));
  const probe = chars[s]?.marks.link ?? (s > 0 ? chars[s - 1]!.marks.link : undefined);
  if (probe === undefined) return null;
  let a = chars[s]?.marks.link === probe ? s : s - 1;
  let b = a;
  while (a > 0 && chars[a - 1]!.marks.link === probe) a--;
  while (b < chars.length && chars[b]!.marks.link === probe) b++;
  return { start: a, end: b, url: probe };
}
